import React, { useEffect, useState } from "react";
import _ from "lodash";
import { useRouteMatch } from "react-router-dom";
import {
  createStyles,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Grid,
  makeStyles,
  TextField,
  Typography,
  useTheme,
} from "@material-ui/core";
import "jspdf-autotable";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import { Autocomplete } from "@material-ui/lab";

import { CustomTheme } from "../../../../types/customTheme";
import TextfieldDefault from "../../../../components/textField/TextfieldDefault";
import DialogCommonDefault from "../../../../components/dialogs/DialogCommonDefault";
import ButtonCommon from "../../../../components/buttons/ButtonCommon";
import {
  fetchCustomerInfoUsingLocationId,
  fetchDisplayInfo,
} from "../../../../services/insightApp/metrics";
import SwitchCommon from "../../../../components/switches/SwitchCommon";
import ValidationMessage from "../../../../components/validation/ValidationMessage";

export interface DashboardCreateModalProps {
  open: any;
  setOpen: any;
  layout: any;
  setLayout: any;
  editableWidget: any;
  setSelectedWidgets: any;
  locationSelectorList: any;
  selectedLocationId: any;
  setSelectedLocationId: any;
  selectedWidgets: any;
}

const useStyles = makeStyles((theme: CustomTheme) =>
  createStyles({
    autoComplete: {
      marginTop: "4px",
      "& .MuiAutocomplete-input": {
        color: theme.palette.custom.orange.contrastText,
      },
      "& .MuiInputBase-root": {
        color: "inherit",
        backgroundColor: theme.palette.background.entity_background,
        borderRadius: 10,
      },
      [`& fieldset`]: {
        borderRadius: 10,
        border: "none",
        color: theme.palette.custom.orange.contrastText,
      },
    },
  }),
);

const WidgetTitleEditRemoveModal: React.FunctionComponent<
  DashboardCreateModalProps
> = ({
  open,
  setOpen,
  layout,
  setLayout,
  editableWidget,
  setSelectedWidgets,
  locationSelectorList,
  selectedLocationId,
  setSelectedLocationId,
  selectedWidgets,
}) => {
  const [label, setLabel] = useState("");
  const [selectedAlign, setSelectedAlign] = useState("center");
  const [selectedStation, setSelectedStation] = useState<any>({});
  const [isOpenLocationId, setIsOpenLocationId] = useState(false);
  const [isOpenStation, setIsOpenStation] = useState(false);
  const [stationNode, setStationNode] = useState<any>({});
  const [errorMessage, setErrorMessage] = useState("");
  const match: any = useRouteMatch();

  const getStationInfo = async () => {
    try {
      // Retrieve the idToken from cookies
      // Fetch all locations using the idToken
      const res = await fetchDisplayInfo(
        selectedLocationId.id || match?.params?.locationId,
      );

      if (res.data.data) {
        const stationNode: any = [];
        Object.values(res.data.data).forEach((item: any) => {
          stationNode.push({
            id: item.code,
            label: item.name,
          });
        });

        setStationNode(stationNode);
        stationNode.map((item: any) => {
          if (item.id === editableWidget?.path?.[1]) {
            setSelectedStation(item);
          }
        });
      } else {
        setStationNode([]);
        setSelectedStation({});
      }
    } catch (error) {
      setStationNode([]);
      setSelectedStation({});
    }
  };

  useEffect(() => {
    if (editableWidget?.path?.[0] === "stationAverageTime") {
      setStationNode([]);
      getStationInfo();
    }
  }, [selectedLocationId]);

  useEffect(() => {
    if (editableWidget && open) {
      if (editableWidget?.path?.[0] === "stationAverageTime") {
      }
      setLabel(editableWidget.label);
      setSelectedAlign(editableWidget?.align ? editableWidget.align : "center");
      locationSelectorList
        .filter(
          (item: any) =>
            item.id ===
            (editableWidget?.locationId || match?.params?.locationId),
        )
        .map((item: any) =>
          setSelectedLocationId({ id: item.id, label: item.label }),
        );
    }
  }, [editableWidget, open]);

  const getCustomerInfo = async (locationId: any) => {
    try {
      // Retrieve the idToken from cookies
      // Fetch all locations using the idToken
      const res = await fetchCustomerInfoUsingLocationId(locationId);
      if (res?.data?.data?.clientId) {
        const clientIdFromApi = res.data.data.clientId;
        return clientIdFromApi;
      }
    } catch (error) {
      // setError("Please try again later.");
    }
  };

  const handleSave = async () => {
    if (
      editableWidget?.path?.[0] === "stationAverageTime" &&
      !selectedStation?.id
    ) {
      setErrorMessage("Please select a station");
      return;
    }

    const locationId = selectedLocationId?.id || "";
    const clientId = locationId ? await getCustomerInfo(locationId) : "";

    const updatedLayout = layout.map((item: any) => {
      if (item.i !== editableWidget?.key) return item;

      const isStation = editableWidget?.path?.[0] === "stationAverageTime";

      const updatedPath = [...(item.data?.path || [])];
      if (isStation && selectedStation?.id) {
        // Ensure path[1] is replaced with new station ID
        updatedPath[1] = selectedStation.id;
      }
      return {
        ...item,
        label: isStation ? selectedStation?.label : label,
        data: {
          ...item.data,
          align: selectedAlign || "center",
          clientId,
          locationId,
          keyProps: isStation ? selectedStation?.id : editableWidget?.keyProps,
          label: isStation ? selectedStation?.label : label,
          labelInitial: isStation
            ? selectedStation?.label
            : editableWidget?.labelInitial,
          path: updatedPath,
        },
      };
    });

    const cloneSelectedWidgets = _.cloneDeep(selectedWidgets);

    cloneSelectedWidgets.map((data: any) => {
      if (data.key === editableWidget.key) {
        const updatedPath = [...(data?.path || [])];
        const isStation = editableWidget?.path?.[0] === "stationAverageTime";

        if (isStation && selectedStation?.id) {
          // Ensure path[1] is replaced with new station ID
          updatedPath[1] = selectedStation.id;
        }
        data["align"] = selectedAlign || "center";
        data["clientId"] = clientId;
        data["locationId"] = locationId;
        data["keyProps"] = isStation
          ? selectedStation?.id
          : editableWidget?.keyProps;
        data["label"] = isStation ? selectedStation?.label : label;
        data["labelInitial"] = isStation
          ? selectedStation?.label
          : editableWidget?.labelInitial;
        data["path"] = updatedPath;
      }
    });

    setSelectedWidgets(cloneSelectedWidgets);

    setLayout(updatedLayout);
    setOpen(false);
  };

  const handleTitleChange = (e: any) => {
    setLabel(e.target.value);
  };

  const handleActivate = (e: any) => {
    const name = e.target.name;
    setSelectedAlign((prev) => (prev === name ? "" : name));
  };

  const handleChangeLocationId = (e: any, role: any) => {
    setErrorMessage("");
    setSelectedLocationId(role);
  };

  const handleChangeStation = (e: any, role: any) => {
    setErrorMessage("");
    setSelectedStation(role);
  };

  const theme: CustomTheme = useTheme();
  const classes: any = useStyles();

  return (
    <>
      <DialogCommonDefault
        open={open}
        setOpen={setOpen}
        maxWidth={"sm"}
        isNeedFixedHeight={false}
      >
        <DialogContent>
          <Typography variant="h6" style={{ marginBottom: "12px" }}>
            {`Label for ${editableWidget?.labelInitial}`}
          </Typography>
          <Typography variant="body1" style={{ marginLeft: "4px" }}>
            Metric Title
          </Typography>
          <TextfieldDefault
            id=""
            name=""
            type="text"
            value={label}
            onChange={handleTitleChange}
          />
          <Typography variant="body1" style={{ marginTop: "12px" }}>
            Select Location
          </Typography>
          <Autocomplete
            open={isOpenLocationId}
            onOpen={() => setIsOpenLocationId(true)}
            onClose={() => setIsOpenLocationId(false)}
            size={"small"}
            id="locationSelectGlobal"
            color="inherit"
            options={locationSelectorList}
            value={selectedLocationId}
            getOptionLabel={(option: any) => option.label || ""}
            disableClearable
            onChange={handleChangeLocationId}
            classes={{ root: classes.autoComplete }}
            popupIcon={<ArrowDropDownIcon color={"inherit"} />}
            renderOption={(props: any) => {
              return (
                <Grid
                  container
                  {...props}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    zIndex: 0,
                    height: "20px",
                    marginBottom: "4px",
                  }}
                >
                  <Grid item xs={12}>
                    <Typography variant="body2" className={classes.titleColor}>
                      {props.label}
                    </Typography>
                  </Grid>
                </Grid>
              );
            }}
            renderInput={(params: any) => (
              <TextField
                color="inherit"
                {...params}
                label=""
                placeholder="Select Location"
                variant="outlined"
              />
            )}
          />
          {editableWidget?.path?.[0] === "stationAverageTime" && (
            <Typography variant="body1" style={{ marginTop: "12px" }}>
              Select Station
            </Typography>
          )}
          {editableWidget?.path?.[0] === "stationAverageTime" && (
            <Autocomplete
              open={isOpenStation}
              onOpen={() => setIsOpenStation(true)}
              onClose={() => setIsOpenStation(false)}
              size={"small"}
              id="locationSelectGlobal"
              color="inherit"
              options={stationNode}
              value={selectedStation}
              getOptionLabel={(option: any) => option.label || ""}
              disableClearable
              onChange={handleChangeStation}
              classes={{ root: classes.autoComplete }}
              popupIcon={<ArrowDropDownIcon color={"inherit"} />}
              renderOption={(props: any) => {
                return (
                  <Grid
                    container
                    {...props}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      zIndex: 0,
                      height: "20px",
                      marginBottom: "4px",
                    }}
                  >
                    <Grid item xs={12}>
                      <Typography
                        variant="body2"
                        className={classes.titleColor}
                      >
                        {props.label}
                      </Typography>
                    </Grid>
                  </Grid>
                );
              }}
              renderInput={(params: any) => (
                <TextField
                  color="inherit"
                  {...params}
                  label=""
                  placeholder="Select Station"
                  variant="outlined"
                />
              )}
            />
          )}
          {errorMessage && <ValidationMessage message={errorMessage} />}
          <Typography
            variant="body1"
            style={{ marginLeft: "4px", marginTop: "12px" }}
          >
            Text Alignment
          </Typography>
          <FormControlLabel
            style={{ marginRight: "0px" }}
            control={<SwitchCommon />}
            checked={selectedAlign === "left"}
            onChange={handleActivate}
            disabled={false}
            label="Left"
            labelPlacement="top"
            name="left"
          />

          <FormControlLabel
            style={{ marginRight: "0px" }}
            control={<SwitchCommon />}
            checked={selectedAlign === "center"}
            onChange={handleActivate}
            disabled={false}
            label="Centre"
            labelPlacement="top"
            name="center"
          />
          <FormControlLabel
            style={{ marginRight: "0px" }}
            control={<SwitchCommon />}
            checked={selectedAlign === "right"}
            onChange={handleActivate}
            disabled={false}
            label="Right"
            labelPlacement="top"
            name="right"
          />
        </DialogContent>
        <DialogActions style={{ marginBottom: "12px", marginRight: "12px" }}>
          <ButtonCommon
            style={{ width: "120px" }}
            onClick={() => setOpen(false)}
          >
            Cancel
          </ButtonCommon>
          <ButtonCommon
            style={{ width: "120px" }}
            onClick={handleSave}
            color="green"
          >
            Save
          </ButtonCommon>
        </DialogActions>
      </DialogCommonDefault>
    </>
  );
};

export default WidgetTitleEditRemoveModal;
